
/**
 * Manages dropdown toggle behavior, allowing for opening and closing dropdown menus.
 *
 * @example
 * // Initialize a dropdown handler with default settings.
 * const dropdownHandler = new DropdownHandler();
 *
 * @example
 * // Programmatically close a dropdown.
 * dropdownHandler.closeDropdown('#dropdown-menu-id');
 *
 * @access public
 */
class DropdownHandler {
    /**
     * Creates an instance of DropdownHandler.
     * @access public
     * @param {Object} options - Configuration options for the dropdown handler.
     * @param {string} [options.activeClass='is-active'] - CSS class applied to active toggles.
     * @param {string} [options.openClass='is-open'] - CSS class applied to open dropdowns.
     * @param {string} [options.toggleSelector='[data-toggle="dropdown"]'] - Selector for dropdown toggle elements.
     */
    constructor({
        activeClass = 'is-active',
        openClass = 'is-open',
        toggleSelector = '[data-toggle="dropdown"]'
    } = {}) {
        this.activeClass = activeClass;
        this.openClass = openClass;
        this.toggleSelector = toggleSelector;
        this.dropdownToggles = [];
        this.initDropdowns();
    }

    /**
     * Initializes dropdowns by finding toggle elements and setting up their behavior.
     * @access private
     */
    initDropdowns() {
        this.dropdownToggles = [...document.querySelectorAll(this.toggleSelector)];

        this.dropdownToggles.forEach(toggle => {
            const targetSelector = toggle.getAttribute('data-target');
            const targetElement = document.querySelector(targetSelector);

            if(!targetSelector) {
                console.warn(`Target item not found: ${targetSelector}`);
                return;
            }

            this.setupDropdown(toggle, targetElement);

        });
    }

    /**
     * Sets up a specific dropdown toggle and its target element.
     * Adds event listeners for click and keyboard interactions.
     *
     * @access private
     * @param {HTMLElement} toggle - The dropdown toggle element.
     * @param {HTMLElement} targetElement - The dropdown content element.
     */
    setupDropdown(toggle, targetElement) {
        targetElement.style.overflow = 'hidden';
        targetElement.style.transition = 'height 0.3s ease-out';

        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        this.setDropdownState(toggle, targetElement, isExpanded);

        const toggleFunction = (event) => {
            event.preventDefault();
            const isOpen = targetElement.classList.contains(this.openClass);
            this.setDropdownState(toggle, targetElement, !isOpen);
        };

        toggle.addEventListener('click', toggleFunction);
        toggle.addEventListener('keydown', (event) => {
            if (['Enter', ' '].includes(event.key)) {
                toggleFunction(event);
            }
        });
    }

    /**
     * Sets the state of a dropdown, either opening or closing it.
     * Adjusts CSS classes and `aria-expanded` attributes.
     *
     * @access private
     * @param {HTMLElement} toggle - The dropdown toggle element.
     * @param {HTMLElement} targetElement - The dropdown content element.
     * @param {boolean} isOpen - Whether the dropdown should be open.
     */
    setDropdownState(toggle, targetElement, isOpen) {
        targetElement.style.height = isOpen ? `${targetElement.scrollHeight}px` : '0';
        targetElement.classList.toggle(this.openClass, isOpen);
        toggle.classList.toggle(this.activeClass, isOpen);
        toggle.setAttribute('aria-expanded', isOpen);
    }

    /**
     * Closes a dropdown programmatically.
     * Finds the target dropdown by selector and sets its state to closed.
     *
     * @access public
     * @param {string} targetSelector - CSS selector for the dropdown content to be closed.
     */
    closeDropdown(targetSelector) {
        const targetElement = document.querySelector(targetSelector);
        const toggle = this.dropdownToggles.find(t => t.getAttribute('data-target') === targetSelector);
        if (targetElement && toggle) {
            this.setDropdownState(toggle, targetElement, false);
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {

    window.ASB_App = window.ASB_App || {};
    window.ASB_App.dropdownHandler = new DropdownHandler();

});
